import type { RangeOpt } from '../../modules/DateRangeSelect';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes — shared types for the two list screens.
// Port of legacy ALPACA CRMQuotes_ListView_pendingsubmission / _Tracking.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The Open Quotes date chip — legacy `QuotesDateFilter`
 * (QuoteViewController.Designer.cs :124-158).
 *
 * FIVE options. The legacy handler also has `2Y` / `5Y` branches
 * (QuoteViewController :7285-7292) but declares no items that raise them, so
 * they are dead code and are not offered. Deliberately a SHORTER list than the
 * Reagent Prep chip, which has seven.
 */
export type QuoteDateFilter = 'All' | '1M' | '3M' | '6M' | '1Y';

/**
 * The chip row, in the order legacy declares the items.
 *
 * `days` is only the shared component's identity key — the API is driven by
 * `label`, and the real cutoff is computed server-side by the shared
 * `dbo.AI_DateFilterCutoff`, in ONE place. So these numbers need not be exact
 * calendar arithmetic.
 */
export const QUOTE_RANGES: RangeOpt[] = [
  { label: 'All', days: null },
  { label: '1M',  days: 30 },
  { label: '3M',  days: 90 },
  { label: '6M',  days: 180 },
  { label: '1Y',  days: 365 },
];

/** The chip Open Quotes opens on, matching the live GoLive screen. */
export const QUOTE_DEFAULT_FILTER: QuoteDateFilter = '1M';
/** Quotes Tracking opens on 3M — the BO UI Standard v4 §17 default for every
 *  Tracking screen (owner decision 2026-10-14). */
export const QUOTE_TRACKING_DEFAULT_FILTER: QuoteDateFilter = '3M';

/** Chip code → the shared component's `days` key. */
export function rangeDays(code: QuoteDateFilter): number | null {
  return QUOTE_RANGES.find((r) => r.label === code)?.days ?? null;
}

/**
 * The shared component's `days` key → chip code.
 *
 * `null` is a REAL value here ("All"), not "unknown", so it is matched
 * explicitly before the lookup — `?? QUOTE_DEFAULT_FILTER` on a null-days match
 * would turn the All chip into 1M and silently hide most of the list.
 */
export function rangeCode(days: number | null): QuoteDateFilter {
  if (days === null) return 'All';
  return (QUOTE_RANGES.find((r) => r.days === days)?.label ?? QUOTE_DEFAULT_FILTER) as QuoteDateFilter;
}

/**
 * One row of EITHER quote list — the two legacy views carry the identical nine
 * visible columns (Model.xafml :14104 / :14471); only their server-side criteria
 * differ, so one row type serves both.
 */
export interface QuoteListRow {
  /** dbo.CRMQuotes key — row identity; not a displayed column. */
  oid: string;
  /** Legacy caption "Date". */
  quotedDate: string | null;
  /** CRMQuotes.Division resolved to Company.CompanyName. */
  division: string | null;
  quoteID: string | null;
  title: string | null;
  /**
   * Legacy caption "Client" — the [NonPersistent] `NonClient`: the PROSPECT's
   * name when `isProspect`, the customer's name otherwise. There is no such
   * column on the table; the server composes it.
   */
  client: string | null;
  /** Legacy caption "Prospect". Also decides what `client` holds. */
  isProspect: boolean;
  /** Legacy caption "Amount" — a SQL `money` column. */
  quotedAmount: number | null;
  expirationDate: string | null;
  /** Raw CRMQuotes.Status ordinal — stable across renames. */
  statusCode: number | null;
  /**
   * The status as the lab reads it. NOT the enum member name: XafDisplayName
   * renames `PendingReview` → "Pending Validation" and `Reviewed` → "Won".
   */
  status: string | null;
}
